import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router'

import { AuthLayout } from '@/app/layouts/AuthLayout'
import { AdminLayout } from '@/app/layouts/AdminLayout'
import { JudgeLayout } from '@/app/layouts/JudgeLayout'
import { CompetitionTabsLayout } from '@/app/layouts/CompetitionTabsLayout'
import { RequireAdmin } from '@/app/guards/RequireAdmin'
import { RequireJudge } from '@/app/guards/RequireJudge'
import { RootRedirect } from '@/app/guards/RootRedirect'
import { LogoutAction } from '@/app/guards/LogoutAction'

// Auth pages
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'))
const MagicLinkPage = lazy(() => import('@/pages/auth/MagicLinkPage'))
const AccessRevokedPage = lazy(() => import('@/pages/auth/AccessRevokedPage'))
const NotFoundPage = lazy(() => import('@/pages/shared/NotFoundPage'))

// Admin - Master Data
const StudentsListPage = lazy(() => import('@/pages/admin/students/StudentsListPage'))
const StudentFormPage = lazy(() => import('@/pages/admin/students/StudentFormPage'))
const StudentsImportPage = lazy(() => import('@/pages/admin/students/StudentsImportPage'))
const JudgesListPage = lazy(() => import('@/pages/admin/judges/JudgesListPage'))
const JudgeFormPage = lazy(() => import('@/pages/admin/judges/JudgeFormPage'))
const AdminSettingsPage = lazy(() => import('@/pages/admin/AdminSettingsPage'))

// Admin - Competitions
const CompetitionsListPage = lazy(() => import('@/pages/admin/competitions/CompetitionsListPage'))
const NewCompetitionPage = lazy(() => import('@/pages/admin/competitions/NewCompetitionPage'))
const SettingsPage = lazy(() => import('@/pages/admin/competitions/SettingsPage'))
const CategoriesPage = lazy(() => import('@/pages/admin/competitions/CategoriesPage'))
const ScoringConfigPage = lazy(() => import('@/pages/admin/competitions/ScoringConfigPage'))
const ParticipantsPage = lazy(() => import('@/pages/admin/competitions/ParticipantsPage'))
const ParticipantsImportPage = lazy(
  () => import('@/pages/admin/competitions/ParticipantsImportPage'),
)
const JudgeParticipationsPage = lazy(
  () => import('@/pages/admin/competitions/JudgeParticipationsPage'),
)
const LiveViewPage = lazy(() => import('@/pages/admin/competitions/LiveViewPage'))
const ScoreOverridePage = lazy(() => import('@/pages/admin/competitions/ScoreOverridePage'))
const ResultsPage = lazy(() => import('@/pages/admin/competitions/ResultsPage'))
const AuditLogPage = lazy(() => import('@/pages/admin/competitions/AuditLogPage'))

// Judge pages
const CompetitionHomePage = lazy(() => import('@/pages/judge/CompetitionHomePage'))
const CategoryViewPage = lazy(() => import('@/pages/judge/CategoryViewPage'))
const ScoringPage = lazy(() => import('@/pages/judge/ScoringPage'))

function PageLoader(): React.ReactElement {
  return (
    <div className="flex min-h-[200px] items-center justify-center">
      <div className="text-muted-foreground">جارٍ التحميل...</div>
    </div>
  )
}

export function AppRouter(): React.ReactElement {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Root redirect */}
        <Route index element={<RootRedirect />} />

        {/* Logout action */}
        <Route path="logout" element={<LogoutAction />} />

        {/* Auth routes */}
        <Route element={<AuthLayout />}>
          <Route path="login" element={<LoginPage />} />
          <Route path="j/:token" element={<MagicLinkPage />} />
          <Route path="access-revoked" element={<AccessRevokedPage />} />
        </Route>

        {/* Admin routes */}
        <Route
          path="admin"
          element={
            <RequireAdmin>
              <AdminLayout />
            </RequireAdmin>
          }
        >
          <Route index element={<Navigate to="competitions" replace />} />

          <Route path="students">
            <Route index element={<StudentsListPage />} />
            <Route path="new" element={<StudentFormPage />} />
            <Route path=":id" element={<StudentFormPage />} />
            <Route path="import" element={<StudentsImportPage />} />
          </Route>

          <Route path="judges">
            <Route index element={<JudgesListPage />} />
            <Route path="new" element={<JudgeFormPage />} />
            <Route path=":id" element={<JudgeFormPage />} />
          </Route>

          <Route path="settings" element={<AdminSettingsPage />} />

          <Route path="competitions">
            <Route index element={<CompetitionsListPage />} />
            <Route path="new" element={<NewCompetitionPage />} />
            <Route path=":id" element={<CompetitionTabsLayout />}>
              <Route index element={<Navigate to="settings" replace />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="categories" element={<CategoriesPage />} />
              <Route path="scoring-config" element={<ScoringConfigPage />} />
              <Route path="participants" element={<ParticipantsPage />} />
              <Route path="participants/import" element={<ParticipantsImportPage />} />
              <Route path="judges" element={<JudgeParticipationsPage />} />
              <Route path="live" element={<LiveViewPage />} />
              <Route path="live/participants/:pid/scores" element={<ScoreOverridePage />} />
              <Route path="results" element={<ResultsPage />} />
              <Route path="audit" element={<AuditLogPage />} />
            </Route>
          </Route>
        </Route>

        {/* Judge routes */}
        <Route
          path="competition"
          element={
            <RequireJudge>
              <JudgeLayout />
            </RequireJudge>
          }
        >
          <Route index element={<CompetitionHomePage />} />
          <Route path="categories/:categoryId" element={<CategoryViewPage />} />
          <Route path="participants/:pid/score" element={<ScoringPage />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  )
}
